import React, { useState, useEffect } from 'react';
import { Bell, Lock, Database, Download, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { exportSamplesToExcel } from '../utils/excelExport';
import { exportDewarsToExcel } from '../utils/excelExport';
import { useAuth } from '../contexts/AuthContextAppwrite';
import { useSettings } from '../contexts/SettingsContext';

const Settings = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { settings, updateSetting: updateGlobalSetting } = useSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Redirect non-admin users
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, isAdmin, navigate]);

  const handleToggleSetting = async (settingKey) => {
    if (!user?.id || !settings) return;
    
    try {
      setError('');
      setSuccessMessage('');
      setLoading(true);
      
      const newValue = !settings?.[settingKey];
      await updateGlobalSetting(settingKey, newValue);
      
      setSuccessMessage('Setting updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(`Failed to update setting: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNumericSetting = async (settingKey, value) => {
    if (!user?.id || !settings) return;
    
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 1) {
      setError('Please enter a valid number greater than 0');
      return;
    }
    
    try {
      setError('');
      setSuccessMessage('');
      setLoading(true);
      
      await updateGlobalSetting(settingKey, numValue);
      
      setSuccessMessage('Setting updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(`Failed to update setting: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportAllData = () => {
    // Get data from localStorage
    const samplesData = localStorage?.getItem('sampleData');
    const dewarsData = localStorage?.getItem('dewars');
    
    const samples = samplesData ? JSON.parse(samplesData) : [];
    const dewars = dewarsData ? JSON.parse(dewarsData) : [];
    
    // Export samples if available
    if (samples?.length > 0) {
      exportSamplesToExcel(samples, 'all_samples_export');
    }
    
    // Export dewars if available
    if (dewars?.length > 0) {
      exportDewarsToExcel(dewars, 'all_dewars_export');
    }
    
    if (samples?.length === 0 && dewars?.length === 0) {
      alert('No data available to export');
    }
  };

  const settingsSections = [
    {
      title: 'Notifications',
      icon: <Bell className="w-5 h-5 sm:w-6 sm:h-6" />,
      description: 'Configure alert preferences',
      settings: [
        { label: 'Email Notifications', key: 'emailNotifications', type: 'toggle' },
        { label: 'Temperature Alerts', key: 'temperatureAlerts', type: 'toggle' },
        { label: 'Sample Movement Alerts', key: 'sampleMovementAlerts', type: 'toggle' }
      ]
    },
    {
      title: 'Security',
      icon: <Lock className="w-5 h-5 sm:w-6 sm:h-6" />,
      description: 'Manage security settings',
      settings: [
        { 
          label: 'Session Timeout (minutes)', 
          key: 'sessionTimeoutMinutes', 
          type: 'number',
          min: 1,
          max: 1440,
          placeholder: 'Enter timeout in minutes'
        }
      ]
    },
    {
      title: 'Database',
      icon: <Database className="w-5 h-5 sm:w-6 sm:h-6" />,
      description: 'Database configuration',
      settings: [
        { label: 'Auto Backup', key: 'autoBackup', type: 'toggle' },
        { label: 'Data Retention Policy', key: 'dataRetentionPolicy', type: 'toggle' },
        { label: 'Export Data', key: 'exportData', type: 'toggle' }
      ]
    }
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8 flex items-center justify-center">
        <div className="text-gray-600">Loading settings...</div>
      </div>
    );
  }

  // Show access denied message if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              You do not have permission to access the Settings page. This page is restricted to administrators only.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">Configure application preferences</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700 text-sm">{successMessage}</p>
          </div>
        )}

        {/* Export Data Section */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900">Data Export</h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Export all application data to Excel spreadsheets</p>
            </div>
            <button
              onClick={handleExportAllData}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Export All Data
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {settingsSections?.map((section, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 mr-3 sm:mr-4">
                  {section?.icon}
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">{section?.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">{section?.description}</p>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                {section?.settings?.map((setting, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{setting?.label}</span>
                    
                    {setting?.type === 'number' ? (
                      <input
                        type="number"
                        value={settings?.[setting?.key] || ''}
                        onChange={(e) => handleNumericSetting(setting?.key, e?.target?.value)}
                        min={setting?.min}
                        max={setting?.max}
                        placeholder={setting?.placeholder}
                        disabled={!settings}
                        className="w-24 sm:w-32 px-3 py-2 border border-gray-300 rounded-md text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    ) : (
                      <button
                        onClick={() => handleToggleSetting(setting?.key)}
                        disabled={!settings}
                        className={`relative inline-flex h-5 w-10 sm:h-6 sm:w-11 items-center rounded-full transition-colors ${
                          settings?.[setting?.key] ? 'bg-indigo-600' : 'bg-gray-300'
                        } ${!settings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform ${
                            settings?.[setting?.key] ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Settings;